import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SciStatGrid, SciTextArea } from "@/lib/science/sci-ui";
import { cleanSequence } from "@/lib/science/sequence";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { baseCounts, gcTm, nearestNeighborTm, wallaceTm } from "../lib/tm";

const numCls =
  "w-32 border-black/15 bg-white text-neutral-900 hover:border-black/25 dark:border-black/15 dark:bg-white dark:text-neutral-900";

export function TmCalc() {
  const locale = getLocale();
  const [seq, setSeq] = useState("");
  const [na, setNa] = useState("50");
  const [conc, setConc] = useState("500");

  const out = useMemo(() => {
    const clean = cleanSequence(seq).replace(/U/g, "T");
    const naM = (Number(na) || 0) / 1000;
    const ctM = (Number(conc) || 0) * 1e-9;
    const nn = nearestNeighborTm(seq, naM, ctM);
    const { a, c, g, t } = baseCounts(clean);
    const len = a + c + g + t;
    const gcPct = len > 0 ? ((g + c) / len) * 100 : 0;
    return { nn, wallace: wallaceTm(seq), gc: gcTm(seq), len, gcPct };
  }, [seq, na, conc]);

  const deg = (n: number | null) =>
    n == null
      ? "—"
      : `${n.toLocaleString(locale, { maximumFractionDigits: 1 })} °C`;

  return (
    <div className="sci-card rounded-2xl p-6 sm:p-8">
      <SciTextArea
        id="tm-input"
        label={m.tm_input_label()}
        value={seq}
        placeholder={m.tm_input_placeholder()}
        onChange={setSeq}
        rows={3}
      />

      <div className="mt-4 flex flex-wrap gap-4">
        <div className="space-y-1.5">
          <Label htmlFor="tm-na" className="text-muted-foreground">
            {m.tm_na_label()}
          </Label>
          <Input
            id="tm-na"
            type="number"
            value={na}
            onChange={(e) => setNa(e.target.value)}
            className={numCls}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tm-conc" className="text-muted-foreground">
            {m.tm_conc_label()}
          </Label>
          <Input
            id="tm-conc"
            type="number"
            value={conc}
            onChange={(e) => setConc(e.target.value)}
            className={numCls}
          />
        </div>
      </div>

      {out.len > 1 && out.nn != null ? (
        <>
          <div className="sci-result-zone mt-5 flex items-baseline justify-between gap-4 rounded-xl px-5 py-4">
            <span className="text-sm font-medium text-muted-foreground">
              {m.tm_nn_label()}
            </span>
            <span
              data-testid="tm-nn"
              className="sci-result font-display text-3xl font-bold sm:text-4xl"
            >
              {deg(out.nn)}
            </span>
          </div>
          <SciStatGrid
            items={[
              { label: m.tm_wallace_label(), value: deg(out.wallace) },
              { label: m.tm_gc_label(), value: deg(out.gc) },
              {
                label: m.tm_length_label(),
                value: out.len.toLocaleString(locale),
              },
              {
                label: m.tm_gcpct_label(),
                value: `${out.gcPct.toLocaleString(locale, { maximumFractionDigits: 1 })}%`,
              },
            ]}
          />
        </>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">
          {m.tm_hint_empty()}
        </p>
      )}
    </div>
  );
}
